//#region imports
import React, { useState } from 'react';
import { Button, Modal, Form, Badge } from 'react-bootstrap';

// styling
import { Colors } from '_styles';
import { useIsMobile } from '_utils/hooks';
//#endregion

const ROLE_OPTIONS = [
    { value: 'REPORTS_ONLY', label: 'Reports' },
    { value: 'SECURITY', label: 'Security' },
    { value: 'ALLOCATOR', label: 'Allocator' },
    { value: 'DESK_HOST', label: 'Desk Host' },
    { value: 'OPERATIONS_ADMINISTRATOR', label: 'Operations Administrator' },
];

const EmployeeRow = ({ employee, isCurrentUser, onRoleChange, onDelete, onResendInvite, onRequireMfa, onSendPasswordReset, loading = false, selected = false, onSelectToggle }) => {

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // phones: name/email on top, role + actions wrap onto the lines below
    const isMobile = useIsMobile();

    const isMissingFirebase = employee.missingFirebaseAccount === true;

    const displayName = employee.name && employee.name !== 'N/A'
        ? employee.name
        : employee.email;

    const initials = (employee.name && employee.name !== 'N/A' ? employee.name : employee.email || '?')
        .split(' ')
        .map(part => part.charAt(0))
        .slice(0, 2)
        .join('')
        .toUpperCase();

    const roleInOptions = ROLE_OPTIONS.some(option => option.value === employee.role);
    const showRequireMfa = !employee.mfaEnrolled && !employee.mfaRequired;
    const showRoleSelect = !isMissingFirebase && roleInOptions && !isCurrentUser;

    // status badges: inline after the name on desktop, on their own wrapping line under the email on phones
    const badgeClass = isMobile ? '' : 'ms-2';
    const statusBadges = [
        isMissingFirebase && <Badge key="missing" bg="danger" className={badgeClass}>Missing Firebase Account</Badge>,
        !isMissingFirebase && employee.pending && <Badge key="pending" bg="warning" text="dark" className={badgeClass}>Setup Pending</Badge>,
        !isMissingFirebase && employee.mfaEnrolled && <Badge key="mfa-on" bg="success" className={badgeClass}>MFA On</Badge>,
        !isMissingFirebase && !employee.mfaEnrolled && employee.mfaRequired && <Badge key="mfa-req" bg="info" className={badgeClass}>MFA Required</Badge>,
    ].filter(Boolean);
    // a role that can't be changed here is shown as a badge; on phones it joins the status badges
    const roleBadge = !showRoleSelect && !isMissingFirebase ? <Badge key="role" bg="secondary">{employee.role}</Badge> : null;

    // phones: buttons share the row width
    const actionButtonStyle = isMobile ? { flex: '1 1 auto' } : undefined;
    const hasActions = showRoleSelect || !isCurrentUser || (!isMissingFirebase && (employee.pending || showRequireMfa));

    return (
        <li className={`list-group-item d-flex justify-content-between ${isMobile ? 'flex-column align-items-stretch gap-2 px-2' : 'align-items-center'}`}>
            <div className={`d-flex ${isMobile ? 'align-items-start' : 'align-items-center'}`} style={{ minWidth: 0 }}>

                {/* selection checkbox for bulk actions */}
                {!isCurrentUser ? (
                    <Form.Check
                        type="checkbox"
                        checked={selected}
                        onChange={() => onSelectToggle(employee.id)}
                        className={isMobile ? "me-2 mb-0 mt-2" : "me-2"}
                        disabled={loading}
                    />
                ) : (
                    <div className="me-2" style={{ width: 16 }} />
                )}

                {/* initials */}
                <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{
                        width: isMobile ? 38 : 45,
                        height: isMobile ? 38 : 45,
                        borderRadius: '50%',
                        backgroundColor: Colors.PRM_LIGHT_BLUE,
                        color: 'white',
                        fontWeight: 'bold',
                        flexShrink: 0,
                    }}
                >
                    {initials}
                </div>

                <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-break">
                        {displayName}
                        {isCurrentUser && <span className="text-muted small ms-2">(you)</span>}
                        {!isMobile && statusBadges}
                    </div>
                    <div className="text-muted small text-break">
                        {employee.email}
                    </div>
                    {isMobile && (statusBadges.length > 0 || roleBadge) && (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                            {statusBadges}
                            {roleBadge}
                        </div>
                    )}
                </div>
            </div>

            {(hasActions || !isMobile) && <div className="d-flex flex-wrap align-items-center gap-2">

                {/* role selector (falls back to a label for non-standard roles) */}
                {showRoleSelect ? (
                    <Form.Select
                        size="sm"
                        value={employee.role}
                        onChange={(e) => onRoleChange(employee.id, e.target.value)}
                        disabled={loading}
                        style={{ width: isMobile ? '100%' : 220 }}
                    >
                        {ROLE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                ) : !isMobile ? roleBadge : null}

                {/* resend invite for pending accounts */}
                {!isMissingFirebase && employee.pending && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onResendInvite(employee.id)}
                        disabled={loading}
                        style={actionButtonStyle}
                    >
                        Resend Invite
                    </Button>
                )}

                {/* require password reset + MFA for accounts that do not have MFA yet */}
                {!isMissingFirebase && showRequireMfa && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => onRequireMfa(employee.id)}
                        disabled={loading}
                        style={actionButtonStyle}
                    >
                        Require MFA & Password Reset
                    </Button>
                )}

                {/* send password reset email for accounts with MFA already set up */}
                {!isMissingFirebase && employee.mfaEnrolled && !isCurrentUser && (
                    <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => onSendPasswordReset(employee.email)}
                        disabled={loading}
                        style={actionButtonStyle}
                    >
                        Reset Password
                    </Button>
                )}

                {/* remove / delete member */}
                {!isCurrentUser && (
                    <Button
                        variant={isMissingFirebase ? "danger" : "outline-danger"}
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={loading}
                        style={actionButtonStyle}
                    >
                        {isMissingFirebase ? 'Delete' : 'Remove'}
                    </Button>
                )}
            </div>}

            {/* delete confirmation modal */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isMissingFirebase ? 'Delete Orphaned Account' : 'Remove Member'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isMissingFirebase ? (
                        <>
                            <p>
                                This account (<strong>{employee.firebaseId}</strong>) has no corresponding Firebase account.
                            </p>
                            <p className="text-muted small">
                                This will delete the database record only. There is no Firebase account to remove.
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                Are you sure you want to remove <strong>{displayName}</strong> from the team?
                            </p>
                            <p className="text-muted small">
                                This will delete their account and remove their access to the portal.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => { onDelete(employee.id); setShowDeleteConfirm(false); }}
                    >
                        {isMissingFirebase ? 'Delete Record' : 'Remove Member'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </li>
    );
};

export default EmployeeRow;
